import "./index.scss";

import { Box, MenuItem, SelectChangeEvent, Stack } from "@mui/material";
import Button from "components/Button";
import DatePicker from "components/DatePicker";
import Dropdown from "components/Dropdown";
import Pagination, { PAGE_OPTIONS } from "components/Pagination";
import Table from "components/Table";
import {
  DEFAULT_MEMBER_STATUS,
  EMAIL_VIEW_BY_OPTIONS,
  MYSQL_DATE_FORMAT,
  VIEW_INTERVAL_OPTIONS,
} from "constants/index";
import CustomSelect from "containers/Members/Select";
import GeneratePdf, { GeneratePdfRef } from "containers/Reports/components/GeneratePdf";
import { ReportTableOptions } from "containers/Reports/components/ReportTableOptions";
import { parseISO } from "date-fns";
import { useQueryParams } from "hooks/useQueryParams";
import { GroupForListing } from "models/group";
import { TTCAAppointmentsCompletedReport } from "models/report";
import { TPagination } from "modules/api/type";
import { getGroups } from "modules/group/groupServices";
import { getTCAAppointmentsCompletedReport } from "modules/report/reportService";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  columns,
  columnsForPdf,
  columnsForPrint,
  formatData,
  generateCsvData,
  generateExcelFile,
} from "./data";

const CoachApproachCompletedAppointments = () => {
  const [params, setParams, defaultParams, setObjParams] = useQueryParams({
    date_from: "",
    date_thru: "",
    date_to: "",
    from: Date,
    group_id: "",
    limit: 10,
    page: 1,
    // searchValue: "",
    sort_by: "",
    sort_order: "",
    status: DEFAULT_MEMBER_STATUS,
    thru: Date,
    to: Date,
    view_by: EMAIL_VIEW_BY_OPTIONS[0].value,
    view_interval_options: "", //VIEW_INTERVAL_OPTIONS[0].value,
  });
  const [allGroups, setAllGroups] = useState<GroupForListing[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingAllData, setIsFetchingAllData] = useState(false);
  const [data, setData] = useState<TTCAAppointmentsCompletedReport[]>([]);
  const [allData, setAllData] = useState<TTCAAppointmentsCompletedReport[]>([]);
  const [pagination, setPagination] = useState<TPagination>({
    current_page: 0,
    data_from_offset: 0,
    data_to_offset: 0,
    per_page: PAGE_OPTIONS[0],
    total_pages: 0,
    total_records: 0,
  });
  const pdfRef = useRef<GeneratePdfRef>(null);
  const printRef = useRef<GeneratePdfRef>(null);

  useEffect(() => {
    getGroups({ limit: 0, sort_by: "GROUP_TITLE" }).then((result) => {
      if (result.success) {
        setAllGroups(result.data);
      }
    });

    fetchReport(params);
  }, []);

  const fetchReport = useCallback(
    async (parameters) => {
      if (isFetching) return;
      setIsFetching(true);
      let result = await getTCAAppointmentsCompletedReport(parameters);
      setIsFetching(false);
      if (result.success) {
        setData(formatData(result.data));
        setPagination(result.pagination);
        const tempParams = { ...parameters, limit: 10000 };
        setIsFetchingAllData(true);
        let res = await getTCAAppointmentsCompletedReport(tempParams);
        if (res.success) {
          const formatted = formatData(res.data);
          setAllData(formatted);
        }
        setIsFetchingAllData(false);
      }
    },
    [isFetching]
  );

  const csvData = useMemo(() => {
    return generateCsvData(allData);
  }, [allData]);

  const onExcelFile = (filename) => {
    return generateExcelFile(allData, filename, "TCA Completed Appointments Report");
  };

  //#region event handlers
  const onChangeFilterHandler = (e) => {
    let { name, value } = e.target;
    setParams(name, value);
  };

  const handleStatusChange = (values) => {
    setParams("status", values.join(","));
  };

  const onSubmitFilter = () => {
    let temp = { ...params, page: 1 };
    setParams("page", 1);
    console.log(`params: ${JSON.stringify(params)}`);
    fetchReport(temp);
  };

  const onChangeDateHandler = async (key, m) => {
    const date = moment(m).format(MYSQL_DATE_FORMAT);
    if (date === "Invalid Date") {
      await setParams(key, "");
    } else {
      await setParams(key, date);
    }

    if (key === "date_from") {
      setParams("from", new Date(m));
    }

    if (key === "date_to") {
      setParams("to", new Date(m));
    }

    if (key === "date_thru") {
      setParams("thru", new Date(m));
    }
  };

  const handlePaginationOptionChange = useCallback(
    (event: SelectChangeEvent<any>) => {
      let temp = { ...params, limit: event.target.value, page: 1 };
      setParams("limit", event.target.value);
      fetchReport(temp);
    },
    [fetchReport, params, setParams]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      let temp = { ...params, page };
      setParams("page", page);
      fetchReport(temp);
    },
    [fetchReport, params, setParams]
  );

  const onSortChange = (sortModel) => {
    const { field = "", sort = "" } = sortModel?.length ? sortModel[0] : {};
    let temp = {
      ...params,
      page: 1,
      sort_by: field?.toUpperCase(),
      sort_order: sort?.toUpperCase(),
    };
    setObjParams({
      page: 1,
      sort_by: field?.toUpperCase(),
      sort_order: sort?.toUpperCase(),
    });
    fetchReport(temp);
  };

  const onClickPdfHandler = async () => {
    pdfRef?.current.exportContent();
  };

  const onClickPrintHandler = async () => {
    printRef?.current.printContent();
  };
  //#endregion event handlers

  //console.log("Raw Data Length:", allData.length);
  //console.log("PDF Columns:", columnsForPdf);

  return (
    <>
      <Stack gap={1.25}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="start"
          mt="25px"
          flexWrap="wrap"
          gap="20px"
        >
          <Dropdown
            name="view_by"
            labelId="View By"
            label="View By"
            className=""
            value={params.view_by}
            defaultValue={defaultParams.view_by}
            onChange={onChangeFilterHandler}
          >
            {EMAIL_VIEW_BY_OPTIONS.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Dropdown>
          <CustomSelect defaultOptions={[DEFAULT_MEMBER_STATUS]} handler={handleStatusChange} />
          {/* <Dropdown
            name="group_id"
            labelId="Groups"
            label="Groups"
            className=""
            value={params.group_id}
            defaultValue={defaultParams.members_status}
            onChange={onChangeFilterHandler}
          >
            {allGroups.map((item) => (
              <MenuItem key={item.group_id} value={item.group_id}>
                {item.group_title}
              </MenuItem>
            ))}
          </Dropdown> */}
          {/* <Dropdown
            name="sort_by"
            label="Sort By"
            className=""
            value={params.sort_by}
            defaultValue={defaultParams.sort_by}
            onChange={onChangeFilterHandler}
          >
            <MenuItem value="INITIAL_APPT_DATE">Initial Appointment Date</MenuItem>
            <MenuItem value="INSTRUCTOR_NAME">Instructor Name</MenuItem>
            <MenuItem value="MEMBER">Member</MenuItem>
            <MenuItem value="PCT_APPT_COMPLETED_TO_DATE">Pct. Appts. Completed To Date</MenuItem>
            <MenuItem value="STATUS">Status</MenuItem>
          </Dropdown> */}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="start"
          flexWrap="wrap"
          gap="20px"
        >
          <Dropdown
            name="view_interval_options"
            labelId="Initial Appt. Date Range"
            label="Initial Appt. Date Range"
            className="base-dropdown"
            value={params.view_interval_options}
            defaultValue={defaultParams.view_interval_options}
            onChange={onChangeFilterHandler}
          >
            {VIEW_INTERVAL_OPTIONS.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Dropdown>
          {params.view_interval_options === "CUSTOM" && (
            <>
              <DatePicker
                label="Date From"
                className="base-dropdown"
                value={params.from}
                onChange={(m) => {
                  onChangeDateHandler("date_from", m);
                }}
              />
              <DatePicker
                label="Date To"
                className="base-dropdown"
                value={params.to}
                onChange={(m) => {
                  onChangeDateHandler("date_to", m);
                }}
              />
            </>
          )}
          <DatePicker
            label="Thru-Date"
            className="base-dropdown"
            value={params.thru}
            onChange={(m) => {
              onChangeDateHandler("date_thru", m);
            }}
          />
        </Stack>
        <Button
          label="Refresh Report"
          className="refresh-report-btn"
          color="primary-blue"
          onClick={onSubmitFilter}
        />
      </Stack>
      <ReportTableOptions
        showOptions={!!allData.length}
        title="Report Content"
        type="tca-completed-appointments"
        onClickPdf={onClickPdfHandler}
        onClickPrint={onClickPrintHandler}
        csvData={csvData}
        isLoading={isFetchingAllData}
        onExcelFile={onExcelFile}
      />
      <Table
        rows={data}
        columns={columns}
        sortingMode="server"
        onSortModelChange={onSortChange}
        paginationMode="server"
        pagination
        page={pagination.current_page}
        rowCount={pagination.total_records}
        pageSize={pagination.per_page}
        rowsPerPageOptions={PAGE_OPTIONS}
        onPageChange={handlePageChange}
        disableVirtualization={true}
        paginationProps={{
          ...pagination,
          onPageChange: handlePageChange,
          pageDropdownProps: {
            defaultValue: pagination.per_page,
            onChange: handlePaginationOptionChange,
            value: pagination.per_page,
          },
        }}
        loading={isFetching}
        slots
        headerHeight
      />
      <Pagination
        onPageChange={handlePageChange}
        current_page={pagination.current_page}
        total_pages={pagination.total_pages}
        per_page={pagination.per_page}
        data_from_offset={pagination.data_from_offset}
        data_to_offset={pagination.data_to_offset}
        total_records={pagination.total_records}
        pageDropdownProps={{
          defaultValue: pagination.per_page,
          onChange: handlePaginationOptionChange,
          value: pagination.per_page,
        }}
      />
      <Box className="visually-hidden">
        <GeneratePdf
          ref={pdfRef}
          title="TCA Completed Appointments"
          type="tca-completed-appointments"
          orientation="portrait"
          tableData={{ columns: columnsForPdf, rows: allData }}
          tableProps={{ disableVirtualization: true }}
          className="compact-cells coach-approach-appointment-pdf"
        />
      </Box>
      <Box className="visually-hidden">
        <GeneratePdf
          ref={printRef}
          title="TCA Completed Appointments"
          type="tca-completed-appointments"
          orientation="portrait"
          tableData={{ columns: columnsForPrint, rows: allData }}
          tableProps={{
            autoHeight: true,
            disableVirtualization: true,
          }}
          className="compact-cells coach-approach-appointment-print"
        />
      </Box>
    </>
  );
};

export default CoachApproachCompletedAppointments;
